import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { RootState } from './store';
import type {
  // Auth
  AuthResponse,
  SignupRequest,
  LoginRequest,
  Org,
  // People
  Person,
  PersonCompany,
  CreatePersonInput,
  UpdatePersonInput,
  PersonFilters,
  AddPersonCompanyInput,
  // Companies
  Company,
  CreateCompanyInput,
  UpdateCompanyInput,
  CompanyFilters,
  // Pipelines
  Pipeline,
  StageDefinition,
  CreatePipelineInput,
  UpdatePipelineInput,
  // Leads
  Lead,
  CreateLeadInput,
  UpdateLeadInput,
  LeadFilters,
  ConvertLeadInput,
  ArchiveLeadInput,
  // Deals
  Deal,
  CreateDealInput,
  UpdateDealInput,
  DealFilters,
  UpdateDealStageInput,
  UpdateDealStatusInput,
  DealPerson,
  AddDealPersonInput,
  DealProduct,
  AddDealProductInput,
  UpdateDealProductInput,
  // Products
  Product,
  CreateProductInput,
  UpdateProductInput,
  ProductFilters,
  // Activities
  Activity,
  CreateActivityInput,
  UpdateActivityInput,
  ActivityFilters,
  // Invitations
  Invitation,
  InvitationWithContext,
  CreateInvitationInput,
  AcceptInvitationInput,
  AcceptInvitationWithSignupInput,
  InvitationFilters,
  // Team
  MembershipWithUser,
  Membership,
  UpdateMembershipInput,
  TransferOwnershipInput,
  // Sequences
  Sequence,
  CreateSequenceInput,
  UpdateSequenceInput,
  SequenceFilters,
  SequenceStep,
  CreateSequenceStepInput,
  UpdateSequenceStepInput,
  ReorderStepsInput,
  SequenceEnrollment,
  EnrollPersonInput,
  BulkEnrollInput,
  EnrollmentFilters,
  // Email
  ConnectedEmailAccount,
  EnabledProvider,
  Email,
  EmailThread,
  EmailThreadWithEmails,
  EmailThreadFilters,
  EmailAttachment,
  ComposeEmailInput,
  ReplyEmailInput,
  SyncEmailsInput,
} from './types';

// Helper to build query string from filters
function buildQueryString(filters: Record<string, unknown> | undefined): string {
  if (!filters) return '';
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });
  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

function getBaseUrl(): string {
  if (process.env.NEXT_PUBLIC_API_URL) {
    return process.env.NEXT_PUBLIC_API_URL;
  }
  if (process.env.NODE_ENV === 'production') {
    throw new Error('NEXT_PUBLIC_API_URL must be set in production');
  }
  return 'http://localhost:3000/api';
}

const baseQuery = fetchBaseQuery({
  baseUrl: getBaseUrl(),
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as RootState;
    const token = state.auth.token;
    const orgId = state.auth.org?.id;

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    if (orgId) {
      headers.set('X-Org-Id', orgId);
    }
    return headers;
  },
});

export const api = createApi({
  reducerPath: 'api',
  baseQuery,
  tagTypes: [
    'Person',
    'Company',
    'Pipeline',
    'Stage',
    'Lead',
    'Deal',
    'DealPerson',
    'DealProduct',
    'Product',
    'Activity',
    'Org',
    'Invitation',
    'Team',
    'Sequence',
    'SequenceStep',
    'Enrollment',
    'EmailAccount',
    'EmailThread',
    'EmailAttachment',
  ],
  endpoints: (builder) => ({
    // ==================== Auth ====================
    signup: builder.mutation<AuthResponse, SignupRequest>({
      query: (body) => ({
        url: '/signup',
        method: 'POST',
        body,
      }),
    }),
    login: builder.mutation<AuthResponse, LoginRequest>({
      query: (body) => ({
        url: '/login',
        method: 'POST',
        body,
      }),
    }),
    forgotPassword: builder.mutation<{ message: string }, { email: string }>({
      query: (body) => ({
        url: '/forgot-password',
        method: 'POST',
        body,
      }),
    }),
    validateResetToken: builder.query<{ valid: boolean }, string>({
      query: (token) => `/reset-password/${token}`,
    }),
    resetPassword: builder.mutation<{ message: string }, { token: string; password: string }>({
      query: (body) => ({
        url: '/reset-password',
        method: 'POST',
        body,
      }),
    }),

    // ==================== Org ====================
    getOrg: builder.query<Org, void>({
      query: () => '/org',
      providesTags: ['Org'],
    }),
    updateOrg: builder.mutation<Org, Partial<Org>>({
      query: (body) => ({
        url: '/org',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Org'],
    }),

    // ==================== People ====================
    getPeople: builder.query<Person[], PersonFilters | void>({
      query: (filters) => `/people${buildQueryString(filters as Record<string, unknown>)}`,
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'Person' as const, id })), { type: 'Person', id: 'LIST' }]
          : [{ type: 'Person', id: 'LIST' }],
    }),
    getPerson: builder.query<Person, string>({
      query: (id) => `/people/${id}`,
      providesTags: (result, error, id) => [{ type: 'Person', id }],
    }),
    createPerson: builder.mutation<Person, CreatePersonInput>({
      query: (body) => ({
        url: '/people',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Person', id: 'LIST' }],
    }),
    updatePerson: builder.mutation<Person, { id: string; data: UpdatePersonInput }>({
      query: ({ id, data }) => ({
        url: `/people/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Person', id }, { type: 'Person', id: 'LIST' }],
    }),
    deletePerson: builder.mutation<void, string>({
      query: (id) => ({
        url: `/people/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Person', id: 'LIST' }],
    }),
    getPersonCompanies: builder.query<PersonCompany[], string>({
      query: (personId) => `/people/${personId}/companies`,
      providesTags: (result, error, personId) => [{ type: 'Person', id: `${personId}-companies` }],
    }),
    addPersonCompany: builder.mutation<PersonCompany, { personId: string; data: AddPersonCompanyInput }>({
      query: ({ personId, data }) => ({
        url: `/people/${personId}/companies`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { personId }) => [{ type: 'Person', id: `${personId}-companies` }],
    }),
    removePersonCompany: builder.mutation<void, { personId: string; companyId: string }>({
      query: ({ personId, companyId }) => ({
        url: `/people/${personId}/companies/${companyId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { personId }) => [{ type: 'Person', id: `${personId}-companies` }],
    }),
    getPersonLeads: builder.query<Lead[], string>({
      query: (personId) => `/people/${personId}/leads`,
      providesTags: (result, error, personId) => [{ type: 'Lead', id: `person-${personId}` }],
    }),

    // ==================== Companies ====================
    getCompanies: builder.query<Company[], CompanyFilters | void>({
      query: (filters) => `/companies${buildQueryString(filters as Record<string, unknown>)}`,
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'Company' as const, id })), { type: 'Company', id: 'LIST' }]
          : [{ type: 'Company', id: 'LIST' }],
    }),
    getCompany: builder.query<Company, string>({
      query: (id) => `/companies/${id}`,
      providesTags: (result, error, id) => [{ type: 'Company', id }],
    }),
    createCompany: builder.mutation<Company, CreateCompanyInput>({
      query: (body) => ({
        url: '/companies',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Company', id: 'LIST' }],
    }),
    updateCompany: builder.mutation<Company, { id: string; data: UpdateCompanyInput }>({
      query: ({ id, data }) => ({
        url: `/companies/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Company', id }, { type: 'Company', id: 'LIST' }],
    }),
    deleteCompany: builder.mutation<void, string>({
      query: (id) => ({
        url: `/companies/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Company', id: 'LIST' }],
    }),
    getCompanyPeople: builder.query<Person[], string>({
      query: (companyId) => `/companies/${companyId}/people`,
      providesTags: (result, error, companyId) => [{ type: 'Company', id: `${companyId}-people` }],
    }),
    getCompanyDeals: builder.query<Deal[], string>({
      query: (companyId) => `/companies/${companyId}/deals`,
      providesTags: (result, error, companyId) => [{ type: 'Deal', id: `company-${companyId}` }],
    }),
    getCompanyActivities: builder.query<Activity[], string>({
      query: (companyId) => `/companies/${companyId}/activities`,
      providesTags: (result, error, companyId) => [{ type: 'Activity', id: `company-${companyId}` }],
    }),
    getCompanyLeads: builder.query<Lead[], string>({
      query: (companyId) => `/companies/${companyId}/leads`,
      providesTags: (result, error, companyId) => [{ type: 'Lead', id: `company-${companyId}` }],
    }),

    // ==================== Pipelines ====================
    getPipelines: builder.query<Pipeline[], void>({
      query: () => '/pipelines',
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'Pipeline' as const, id })), { type: 'Pipeline', id: 'LIST' }]
          : [{ type: 'Pipeline', id: 'LIST' }],
    }),
    getPipeline: builder.query<Pipeline, string>({
      query: (id) => `/pipelines/${id}`,
      providesTags: (result, error, id) => [{ type: 'Pipeline', id }],
    }),
    getDefaultPipeline: builder.query<Pipeline, void>({
      query: () => '/pipelines/default',
      providesTags: [{ type: 'Pipeline', id: 'default' }],
    }),
    createPipeline: builder.mutation<Pipeline, CreatePipelineInput>({
      query: (body) => ({
        url: '/pipelines',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Pipeline', id: 'LIST' }],
    }),
    updatePipeline: builder.mutation<Pipeline, { id: string; data: UpdatePipelineInput }>({
      query: ({ id, data }) => ({
        url: `/pipelines/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Pipeline', id },
        { type: 'Pipeline', id: 'LIST' },
        { type: 'Pipeline', id: 'default' },
      ],
    }),
    deletePipeline: builder.mutation<void, string>({
      query: (id) => ({
        url: `/pipelines/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Pipeline', id: 'LIST' }],
    }),
    getPipelineStages: builder.query<StageDefinition[], string>({
      query: (pipelineId) => `/pipelines/${pipelineId}/stages`,
      providesTags: (result, error, pipelineId) => [{ type: 'Stage', id: pipelineId }],
    }),
    getPipelineDeals: builder.query<Deal[], string>({
      query: (pipelineId) => `/pipelines/${pipelineId}/deals`,
      providesTags: (result, error, pipelineId) => [
        { type: 'Deal', id: `pipeline-${pipelineId}` },
        { type: 'Deal', id: 'LIST' },
      ],
    }),
    getStage: builder.query<StageDefinition, string>({
      query: (stageId) => `/stages/${stageId}`,
      providesTags: (result, error, stageId) => [{ type: 'Stage', id: stageId }],
    }),

    // ==================== Leads ====================
    getLeads: builder.query<Lead[], LeadFilters | void>({
      query: (filters) => `/leads${buildQueryString(filters as Record<string, unknown>)}`,
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'Lead' as const, id })), { type: 'Lead', id: 'LIST' }]
          : [{ type: 'Lead', id: 'LIST' }],
    }),
    getLead: builder.query<Lead, string>({
      query: (id) => `/leads/${id}`,
      providesTags: (result, error, id) => [{ type: 'Lead', id }],
    }),
    createLead: builder.mutation<Lead, CreateLeadInput>({
      query: (body) => ({
        url: '/leads',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Lead', id: 'LIST' }],
    }),
    updateLead: builder.mutation<Lead, { id: string; data: UpdateLeadInput }>({
      query: ({ id, data }) => ({
        url: `/leads/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Lead', id }, { type: 'Lead', id: 'LIST' }],
    }),
    deleteLead: builder.mutation<void, string>({
      query: (id) => ({
        url: `/leads/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Lead', id: 'LIST' }],
    }),
    convertLead: builder.mutation<Deal, { id: string; data: ConvertLeadInput }>({
      query: ({ id, data }) => ({
        url: `/leads/${id}/convert`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Lead', id },
        { type: 'Lead', id: 'LIST' },
        { type: 'Deal', id: 'LIST' },
      ],
    }),
    archiveLead: builder.mutation<Lead, { id: string; data?: ArchiveLeadInput }>({
      query: ({ id, data }) => ({
        url: `/leads/${id}/archive`,
        method: 'POST',
        body: data || {},
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Lead', id }, { type: 'Lead', id: 'LIST' }],
    }),

    // ==================== Deals ====================
    getDeals: builder.query<Deal[], DealFilters | void>({
      query: (filters) => `/deals${buildQueryString(filters as Record<string, unknown>)}`,
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'Deal' as const, id })), { type: 'Deal', id: 'LIST' }]
          : [{ type: 'Deal', id: 'LIST' }],
    }),
    getDeal: builder.query<Deal, string>({
      query: (id) => `/deals/${id}`,
      providesTags: (result, error, id) => [{ type: 'Deal', id }],
    }),
    createDeal: builder.mutation<Deal, CreateDealInput>({
      query: (body) => ({
        url: '/deals',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Deal', id: 'LIST' }],
    }),
    updateDeal: builder.mutation<Deal, { id: string; data: UpdateDealInput }>({
      query: ({ id, data }) => ({
        url: `/deals/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Deal', id }, { type: 'Deal', id: 'LIST' }],
    }),
    deleteDeal: builder.mutation<void, string>({
      query: (id) => ({
        url: `/deals/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Deal', id: 'LIST' }],
    }),
    updateDealStage: builder.mutation<Deal, { id: string; data: UpdateDealStageInput }>({
      query: ({ id, data }) => ({
        url: `/deals/${id}/stage`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Deal', id }, { type: 'Deal', id: 'LIST' }],
    }),
    updateDealStatus: builder.mutation<Deal, { id: string; data: UpdateDealStatusInput }>({
      query: ({ id, data }) => ({
        url: `/deals/${id}/status`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Deal', id }, { type: 'Deal', id: 'LIST' }],
    }),

    // Deal People
    getDealPeople: builder.query<DealPerson[], string>({
      query: (dealId) => `/deals/${dealId}/people`,
      providesTags: (result, error, dealId) => [{ type: 'DealPerson', id: dealId }],
    }),
    addDealPerson: builder.mutation<DealPerson, { dealId: string; data: AddDealPersonInput }>({
      query: ({ dealId, data }) => ({
        url: `/deals/${dealId}/people`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { dealId }) => [
        { type: 'DealPerson', id: dealId },
        { type: 'Deal', id: dealId },
      ],
    }),
    removeDealPerson: builder.mutation<void, { dealId: string; personId: string }>({
      query: ({ dealId, personId }) => ({
        url: `/deals/${dealId}/people/${personId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { dealId }) => [
        { type: 'DealPerson', id: dealId },
        { type: 'Deal', id: dealId },
      ],
    }),

    // Deal Products
    getDealProducts: builder.query<DealProduct[], string>({
      query: (dealId) => `/deals/${dealId}/products`,
      providesTags: (result, error, dealId) => [{ type: 'DealProduct', id: dealId }],
    }),
    addDealProduct: builder.mutation<DealProduct, { dealId: string; data: AddDealProductInput }>({
      query: ({ dealId, data }) => ({
        url: `/deals/${dealId}/products`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { dealId }) => [
        { type: 'DealProduct', id: dealId },
        { type: 'Deal', id: dealId },
      ],
    }),
    updateDealProduct: builder.mutation<DealProduct, { dealId: string; lineId: string; data: UpdateDealProductInput }>({
      query: ({ dealId, lineId, data }) => ({
        url: `/deals/${dealId}/products/${lineId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { dealId }) => [
        { type: 'DealProduct', id: dealId },
        { type: 'Deal', id: dealId },
      ],
    }),
    removeDealProduct: builder.mutation<void, { dealId: string; lineId: string }>({
      query: ({ dealId, lineId }) => ({
        url: `/deals/${dealId}/products/${lineId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { dealId }) => [
        { type: 'DealProduct', id: dealId },
        { type: 'Deal', id: dealId },
      ],
    }),

    // Deal Activities
    getDealActivities: builder.query<Activity[], string>({
      query: (dealId) => `/deals/${dealId}/activities`,
      providesTags: (result, error, dealId) => [{ type: 'Activity', id: `deal-${dealId}` }],
    }),

    // ==================== Products ====================
    getProducts: builder.query<Product[], ProductFilters | void>({
      query: (filters) => `/products${buildQueryString(filters as Record<string, unknown>)}`,
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'Product' as const, id })), { type: 'Product', id: 'LIST' }]
          : [{ type: 'Product', id: 'LIST' }],
    }),
    getProduct: builder.query<Product, string>({
      query: (id) => `/products/${id}`,
      providesTags: (result, error, id) => [{ type: 'Product', id }],
    }),
    createProduct: builder.mutation<Product, CreateProductInput>({
      query: (body) => ({
        url: '/products',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Product', id: 'LIST' }],
    }),
    updateProduct: builder.mutation<Product, { id: string; data: UpdateProductInput }>({
      query: ({ id, data }) => ({
        url: `/products/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Product', id }, { type: 'Product', id: 'LIST' }],
    }),
    deleteProduct: builder.mutation<void, string>({
      query: (id) => ({
        url: `/products/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Product', id: 'LIST' }],
    }),
    searchProducts: builder.query<Product[], { q: string; limit?: number }>({
      query: ({ q, limit }) => `/products/search?q=${encodeURIComponent(q)}${limit ? `&limit=${limit}` : ''}`,
      providesTags: [{ type: 'Product', id: 'SEARCH' }],
    }),

    // ==================== Activities ====================
    getActivities: builder.query<Activity[], ActivityFilters | void>({
      query: (filters) => `/activities${buildQueryString(filters as Record<string, unknown>)}`,
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'Activity' as const, id })), { type: 'Activity', id: 'LIST' }]
          : [{ type: 'Activity', id: 'LIST' }],
    }),
    getActivity: builder.query<Activity, string>({
      query: (id) => `/activities/${id}`,
      providesTags: (result, error, id) => [{ type: 'Activity', id }],
    }),
    createActivity: builder.mutation<Activity, CreateActivityInput>({
      query: (body) => ({
        url: '/activities',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, arg) => {
        const tags: { type: 'Activity'; id: string }[] = [{ type: 'Activity', id: 'LIST' }];
        if (arg.deal_id) {
          tags.push({ type: 'Activity', id: `deal-${arg.deal_id}` });
        }
        if (arg.company_id) {
          tags.push({ type: 'Activity', id: `company-${arg.company_id}` });
        }
        if (arg.person_id) {
          tags.push({ type: 'Activity', id: `person-${arg.person_id}` });
        }
        if (arg.lead_id) {
          tags.push({ type: 'Activity', id: `lead-${arg.lead_id}` });
        }
        return tags;
      },
    }),
    updateActivity: builder.mutation<Activity, { id: string; data: UpdateActivityInput }>({
      query: ({ id, data }) => ({
        url: `/activities/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Activity', id }, { type: 'Activity', id: 'LIST' }],
    }),
    deleteActivity: builder.mutation<void, string>({
      query: (id) => ({
        url: `/activities/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Activity', id: 'LIST' }],
    }),
    markActivityDone: builder.mutation<Activity, { id: string; data: { is_done: boolean } }>({
      query: ({ id, data }) => ({
        url: `/activities/${id}/done`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Activity', id }, { type: 'Activity', id: 'LIST' }],
    }),
    toggleActivityPin: builder.mutation<Activity, string>({
      query: (id) => ({
        url: `/activities/${id}/pin`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Activity', id },
        { type: 'Activity', id: 'LIST' },
        { type: 'Activity', id: 'PINNED' },
      ],
    }),
    getPinnedNotes: builder.query<Activity[], { deal_id?: string; person_id?: string; company_id?: string }>({
      query: (params) => `/activities/pinned${buildQueryString(params)}`,
      providesTags: [{ type: 'Activity', id: 'PINNED' }],
    }),

    // ==================== Invitations ====================
    getInvitations: builder.query<InvitationWithContext[], InvitationFilters | void>({
      query: (filters) => `/invitations${buildQueryString(filters as Record<string, unknown>)}`,
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'Invitation' as const, id })), { type: 'Invitation', id: 'LIST' }]
          : [{ type: 'Invitation', id: 'LIST' }],
    }),
    getInvitationByToken: builder.query<InvitationWithContext, string>({
      query: (token) => `/invitations/token/${token}`,
      providesTags: (result) => (result ? [{ type: 'Invitation', id: result.id }] : []),
    }),
    createInvitation: builder.mutation<Invitation, CreateInvitationInput>({
      query: (body) => ({
        url: '/invitations',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Invitation', id: 'LIST' }],
    }),
    acceptInvitation: builder.mutation<Invitation, AcceptInvitationInput>({
      query: (body) => ({
        url: '/invitations/accept',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Invitation', id: 'LIST' }, { type: 'Team', id: 'LIST' }],
    }),
    acceptInvitationWithSignup: builder.mutation<AuthResponse, AcceptInvitationWithSignupInput>({
      query: (body) => ({
        url: '/invitations/accept-with-signup',
        method: 'POST',
        body,
      }),
    }),
    revokeInvitation: builder.mutation<Invitation, string>({
      query: (id) => ({
        url: `/invitations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Invitation', id }, { type: 'Invitation', id: 'LIST' }],
    }),
    resendInvitation: builder.mutation<Invitation, string>({
      query: (id) => ({
        url: `/invitations/${id}/resend`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Invitation', id }],
    }),

    // ==================== Team ====================
    getTeamMembers: builder.query<MembershipWithUser[], void>({
      query: () => '/team',
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'Team' as const, id })), { type: 'Team', id: 'LIST' }]
          : [{ type: 'Team', id: 'LIST' }],
    }),
    updateMemberRole: builder.mutation<Membership, { id: string; data: UpdateMembershipInput }>({
      query: ({ id, data }) => ({
        url: `/team/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Team', id }, { type: 'Team', id: 'LIST' }],
    }),
    removeMember: builder.mutation<void, string>({
      query: (id) => ({
        url: `/team/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Team', id: 'LIST' }],
    }),
    transferOwnership: builder.mutation<void, TransferOwnershipInput>({
      query: (body) => ({
        url: '/team/transfer-ownership',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Team', id: 'LIST' }],
    }),
    leaveOrganization: builder.mutation<void, void>({
      query: () => ({
        url: '/team/leave',
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'Team', id: 'LIST' }],
    }),

    // ==================== Sequences ====================
    getSequences: builder.query<Sequence[], SequenceFilters | void>({
      query: (filters) => `/sequences${buildQueryString(filters as Record<string, unknown>)}`,
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'Sequence' as const, id })), { type: 'Sequence', id: 'LIST' }]
          : [{ type: 'Sequence', id: 'LIST' }],
    }),
    getSequence: builder.query<Sequence, string>({
      query: (id) => `/sequences/${id}`,
      providesTags: (result, error, id) => [{ type: 'Sequence', id }],
    }),
    createSequence: builder.mutation<Sequence, CreateSequenceInput>({
      query: (body) => ({
        url: '/sequences',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Sequence', id: 'LIST' }],
    }),
    updateSequence: builder.mutation<Sequence, { id: string; data: UpdateSequenceInput }>({
      query: ({ id, data }) => ({
        url: `/sequences/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Sequence', id }, { type: 'Sequence', id: 'LIST' }],
    }),
    deleteSequence: builder.mutation<void, string>({
      query: (id) => ({
        url: `/sequences/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Sequence', id: 'LIST' }],
    }),
    duplicateSequence: builder.mutation<Sequence, string>({
      query: (id) => ({
        url: `/sequences/${id}/duplicate`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'Sequence', id: 'LIST' }],
    }),

    // ==================== Sequence Steps ====================
    getSequenceSteps: builder.query<SequenceStep[], string>({
      query: (sequenceId) => `/sequences/${sequenceId}/steps`,
      providesTags: (result, error, sequenceId) => [{ type: 'SequenceStep', id: sequenceId }],
    }),
    addSequenceStep: builder.mutation<SequenceStep, { sequenceId: string; data: CreateSequenceStepInput }>({
      query: ({ sequenceId, data }) => ({
        url: `/sequences/${sequenceId}/steps`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { sequenceId }) => [
        { type: 'SequenceStep', id: sequenceId },
        { type: 'Sequence', id: sequenceId },
      ],
    }),
    updateSequenceStep: builder.mutation<SequenceStep, { sequenceId: string; stepId: string; data: UpdateSequenceStepInput }>({
      query: ({ sequenceId, stepId, data }) => ({
        url: `/sequences/${sequenceId}/steps/${stepId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { sequenceId }) => [{ type: 'SequenceStep', id: sequenceId }],
    }),
    deleteSequenceStep: builder.mutation<void, { sequenceId: string; stepId: string }>({
      query: ({ sequenceId, stepId }) => ({
        url: `/sequences/${sequenceId}/steps/${stepId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { sequenceId }) => [
        { type: 'SequenceStep', id: sequenceId },
        { type: 'Sequence', id: sequenceId },
      ],
    }),
    reorderSequenceSteps: builder.mutation<SequenceStep[], { sequenceId: string; data: ReorderStepsInput }>({
      query: ({ sequenceId, data }) => ({
        url: `/sequences/${sequenceId}/steps/reorder`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { sequenceId }) => [{ type: 'SequenceStep', id: sequenceId }],
    }),

    // ==================== Enrollments ====================
    getEnrollments: builder.query<SequenceEnrollment[], EnrollmentFilters | void>({
      query: (filters) => `/enrollments${buildQueryString(filters as Record<string, unknown>)}`,
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'Enrollment' as const, id })), { type: 'Enrollment', id: 'LIST' }]
          : [{ type: 'Enrollment', id: 'LIST' }],
    }),
    getSequenceEnrollments: builder.query<SequenceEnrollment[], string>({
      query: (sequenceId) => `/sequences/${sequenceId}/enrollments`,
      providesTags: (result, error, sequenceId) => [{ type: 'Enrollment', id: `sequence-${sequenceId}` }],
    }),
    enrollContact: builder.mutation<SequenceEnrollment, { sequenceId: string; data: EnrollPersonInput }>({
      query: ({ sequenceId, data }) => ({
        url: `/sequences/${sequenceId}/enroll`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { sequenceId }) => [
        { type: 'Enrollment', id: 'LIST' },
        { type: 'Enrollment', id: `sequence-${sequenceId}` },
        { type: 'Sequence', id: sequenceId },
      ],
    }),
    bulkEnroll: builder.mutation<SequenceEnrollment[], { sequenceId: string; data: BulkEnrollInput }>({
      query: ({ sequenceId, data }) => ({
        url: `/sequences/${sequenceId}/bulk-enroll`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { sequenceId }) => [
        { type: 'Enrollment', id: 'LIST' },
        { type: 'Enrollment', id: `sequence-${sequenceId}` },
        { type: 'Sequence', id: sequenceId },
      ],
    }),
    pauseEnrollment: builder.mutation<SequenceEnrollment, string>({
      query: (id) => ({
        url: `/enrollments/${id}/pause`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Enrollment', id },
        { type: 'Enrollment', id: 'LIST' },
        { type: 'Sequence', id: 'LIST' },
      ],
    }),
    resumeEnrollment: builder.mutation<SequenceEnrollment, string>({
      query: (id) => ({
        url: `/enrollments/${id}/resume`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Enrollment', id },
        { type: 'Enrollment', id: 'LIST' },
        { type: 'Sequence', id: 'LIST' },
      ],
    }),
    unenroll: builder.mutation<void, string>({
      query: (id) => ({
        url: `/enrollments/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Enrollment', id: 'LIST' }, { type: 'Sequence', id: 'LIST' }],
    }),

    // ==================== Email Accounts ====================
    getEmailAccounts: builder.query<ConnectedEmailAccount[], void>({
      query: () => '/email/accounts',
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'EmailAccount' as const, id })), { type: 'EmailAccount', id: 'LIST' }]
          : [{ type: 'EmailAccount', id: 'LIST' }],
    }),
    getEmailAccount: builder.query<ConnectedEmailAccount, string>({
      query: (id) => `/email/accounts/${id}`,
      providesTags: (result, error, id) => [{ type: 'EmailAccount', id }],
    }),
    setDefaultEmailAccount: builder.mutation<void, string>({
      query: (id) => ({
        url: `/email/accounts/${id}/default`,
        method: 'PUT',
      }),
      invalidatesTags: [{ type: 'EmailAccount', id: 'LIST' }],
    }),
    disconnectEmailAccount: builder.mutation<void, string>({
      query: (id) => ({
        url: `/email/accounts/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'EmailAccount', id: 'LIST' }],
    }),
    resetAccountSync: builder.mutation<void, string>({
      query: (id) => ({
        url: `/email/accounts/${id}/reset-sync`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'EmailAccount', id: 'LIST' }],
    }),
    getEnabledProviders: builder.query<EnabledProvider[], void>({
      query: () => '/email/providers',
    }),
    getEmailAuthUrl: builder.query<{ url: string }, string>({
      query: (provider) => `/email/oauth/${provider}/authorize`,
    }),
    exchangeOAuthCode: builder.mutation<ConnectedEmailAccount, { provider: string; code: string; state: string }>({
      query: ({ provider, code, state }) => ({
        url: `/email/oauth/${provider}/callback`,
        method: 'POST',
        body: { code, state },
      }),
      invalidatesTags: [{ type: 'EmailAccount', id: 'LIST' }],
    }),
    getUnreadCount: builder.query<{ count: number }, string>({
      query: (accountId) => `/email/accounts/${accountId}/unread`,
    }),

    // ==================== Email Threads ====================
    getEmailThreads: builder.query<EmailThread[], EmailThreadFilters | void>({
      query: (filters) => `/email/threads${buildQueryString(filters as Record<string, unknown>)}`,
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'EmailThread' as const, id })), { type: 'EmailThread', id: 'LIST' }]
          : [{ type: 'EmailThread', id: 'LIST' }],
    }),
    getEmailThread: builder.query<EmailThreadWithEmails, string>({
      query: (id) => `/email/threads/${id}`,
      providesTags: (result, error, id) => [{ type: 'EmailThread', id }],
    }),
    starEmailThread: builder.mutation<void, { id: string; is_starred: boolean }>({
      query: ({ id, is_starred }) => ({
        url: `/email/threads/${id}/star`,
        method: 'PUT',
        body: { is_starred },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'EmailThread', id }, { type: 'EmailThread', id: 'LIST' }],
    }),
    archiveEmailThread: builder.mutation<void, { id: string; is_archived: boolean }>({
      query: ({ id, is_archived }) => ({
        url: `/email/threads/${id}/archive`,
        method: 'PUT',
        body: { is_archived },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'EmailThread', id }, { type: 'EmailThread', id: 'LIST' }],
    }),
    markEmailThreadRead: builder.mutation<void, { id: string; is_read: boolean }>({
      query: ({ id, is_read }) => ({
        url: `/email/threads/${id}/read`,
        method: 'PUT',
        body: { is_read },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'EmailThread', id }, { type: 'EmailThread', id: 'LIST' }],
    }),
    linkEmailThread: builder.mutation<void, { id: string; person_id?: string; company_id?: string; deal_id?: string }>({
      query: ({ id, ...body }) => ({
        url: `/email/threads/${id}/link`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'EmailThread', id }],
    }),
    deleteEmailThread: builder.mutation<void, string>({
      query: (id) => ({
        url: `/email/threads/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'EmailThread', id: 'LIST' }],
    }),
    bulkDeleteEmailThreads: builder.mutation<{ deleted: number }, string[]>({
      query: (ids) => ({
        url: '/email/threads/bulk-delete',
        method: 'POST',
        body: { ids },
      }),
      invalidatesTags: [{ type: 'EmailThread', id: 'LIST' }],
    }),
    shareEmailThread: builder.mutation<void, string>({
      query: (id) => ({
        url: `/email/threads/${id}/share`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'EmailThread', id }, { type: 'EmailThread', id: 'LIST' }],
    }),
    unshareEmailThread: builder.mutation<void, string>({
      query: (id) => ({
        url: `/email/threads/${id}/unshare`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'EmailThread', id }, { type: 'EmailThread', id: 'LIST' }],
    }),

    // ==================== Emails ====================
    syncEmails: builder.mutation<Email[], SyncEmailsInput>({
      query: (body) => ({
        url: '/email/sync',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'EmailThread', id: 'LIST' }],
    }),
    sendEmail: builder.mutation<Email, ComposeEmailInput>({
      query: (body) => ({
        url: '/email/send',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'EmailThread', id: 'LIST' }],
    }),
    replyEmail: builder.mutation<Email, ReplyEmailInput>({
      query: (body) => ({
        url: '/email/reply',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'EmailThread', id: arg.thread_id },
        { type: 'EmailThread', id: 'LIST' },
      ],
    }),
    fetchEmailContent: builder.mutation<Email, string>({
      query: (id) => ({
        url: `/email/${id}/content`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, arg) =>
        result ? [{ type: 'EmailThread', id: result.thread_id }] : [],
    }),

    // ==================== Email Attachments ====================
    getEmailAttachments: builder.query<EmailAttachment[], string>({
      query: (emailId) => `/email/emails/${emailId}/attachments`,
      providesTags: (result, error, emailId) => [{ type: 'EmailAttachment', id: emailId }],
    }),
  }),
});

export const {
  // Auth
  useSignupMutation,
  useLoginMutation,
  useForgotPasswordMutation,
  useValidateResetTokenQuery,
  useResetPasswordMutation,
  // Org
  useGetOrgQuery,
  useUpdateOrgMutation,
  // People
  useGetPeopleQuery,
  useGetPersonQuery,
  useCreatePersonMutation,
  useUpdatePersonMutation,
  useDeletePersonMutation,
  useGetPersonCompaniesQuery,
  useAddPersonCompanyMutation,
  useRemovePersonCompanyMutation,
  useGetPersonLeadsQuery,
  // Companies
  useGetCompaniesQuery,
  useGetCompanyQuery,
  useCreateCompanyMutation,
  useUpdateCompanyMutation,
  useDeleteCompanyMutation,
  useGetCompanyPeopleQuery,
  useGetCompanyDealsQuery,
  useGetCompanyActivitiesQuery,
  useGetCompanyLeadsQuery,
  // Pipelines
  useGetPipelinesQuery,
  useGetPipelineQuery,
  useGetDefaultPipelineQuery,
  useCreatePipelineMutation,
  useUpdatePipelineMutation,
  useDeletePipelineMutation,
  useGetPipelineStagesQuery,
  useGetPipelineDealsQuery,
  useGetStageQuery,
  // Leads
  useGetLeadsQuery,
  useGetLeadQuery,
  useCreateLeadMutation,
  useUpdateLeadMutation,
  useDeleteLeadMutation,
  useConvertLeadMutation,
  useArchiveLeadMutation,
  // Deals
  useGetDealsQuery,
  useGetDealQuery,
  useCreateDealMutation,
  useUpdateDealMutation,
  useDeleteDealMutation,
  useUpdateDealStageMutation,
  useUpdateDealStatusMutation,
  useGetDealPeopleQuery,
  useAddDealPersonMutation,
  useRemoveDealPersonMutation,
  useGetDealProductsQuery,
  useAddDealProductMutation,
  useUpdateDealProductMutation,
  useRemoveDealProductMutation,
  useGetDealActivitiesQuery,
  // Products
  useGetProductsQuery,
  useGetProductQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
  useSearchProductsQuery,
  // Activities
  useGetActivitiesQuery,
  useGetActivityQuery,
  useCreateActivityMutation,
  useUpdateActivityMutation,
  useDeleteActivityMutation,
  useMarkActivityDoneMutation,
  useToggleActivityPinMutation,
  useGetPinnedNotesQuery,
  // Invitations
  useGetInvitationsQuery,
  useGetInvitationByTokenQuery,
  useCreateInvitationMutation,
  useAcceptInvitationMutation,
  useAcceptInvitationWithSignupMutation,
  useRevokeInvitationMutation,
  useResendInvitationMutation,
  // Team
  useGetTeamMembersQuery,
  useUpdateMemberRoleMutation,
  useRemoveMemberMutation,
  useTransferOwnershipMutation,
  useLeaveOrganizationMutation,
  // Sequences
  useGetSequencesQuery,
  useGetSequenceQuery,
  useCreateSequenceMutation,
  useUpdateSequenceMutation,
  useDeleteSequenceMutation,
  useDuplicateSequenceMutation,
  // Sequence Steps
  useGetSequenceStepsQuery,
  useAddSequenceStepMutation,
  useUpdateSequenceStepMutation,
  useDeleteSequenceStepMutation,
  useReorderSequenceStepsMutation,
  // Enrollments
  useGetEnrollmentsQuery,
  useGetSequenceEnrollmentsQuery,
  useEnrollContactMutation,
  useBulkEnrollMutation,
  usePauseEnrollmentMutation,
  useResumeEnrollmentMutation,
  useUnenrollMutation,
  // Email Accounts
  useGetEmailAccountsQuery,
  useGetEmailAccountQuery,
  useSetDefaultEmailAccountMutation,
  useDisconnectEmailAccountMutation,
  useResetAccountSyncMutation,
  useGetEnabledProvidersQuery,
  useGetEmailAuthUrlQuery,
  useLazyGetEmailAuthUrlQuery,
  useExchangeOAuthCodeMutation,
  useGetUnreadCountQuery,
  // Email Threads
  useGetEmailThreadsQuery,
  useGetEmailThreadQuery,
  useStarEmailThreadMutation,
  useArchiveEmailThreadMutation,
  useMarkEmailThreadReadMutation,
  useLinkEmailThreadMutation,
  useDeleteEmailThreadMutation,
  useBulkDeleteEmailThreadsMutation,
  useShareEmailThreadMutation,
  useUnshareEmailThreadMutation,
  // Emails
  useSyncEmailsMutation,
  useSendEmailMutation,
  useReplyEmailMutation,
  useFetchEmailContentMutation,
  // Email Attachments
  useGetEmailAttachmentsQuery,
} = api;
